import { useState } from 'react';
import { Card, Table, Button, Space, Typography, Input, Checkbox, Switch, Tag, Popconfirm, Tooltip, message } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useConsentAdmin } from '../../hooks/use_consent_admin';
import { useNodeSettingsAdmin } from '../../hooks/use_node_settings_admin';
import { PLATFORM_LEGAL_ENTITY_NAME } from '../../hooks/use_node_settings';
import InlineEditField from '../Entities/InlineEditField';

const { Title, Text, Paragraph } = Typography;

/**
 * Superadmin-only list of the consents every user is prompted with before
 * using the app. Required consents must be accepted to continue; optional
 * ones are recorded either way. Editing a consent's title or wording bumps
 * its version, so everyone is asked again. Rendered inside the Node settings
 * "Consents" tab, which only superadmins see.
 *
 * Also carries the node's legal entity name — the one thing every node's
 * Terms/Privacy pages and footer need to differ on. Set it once here and
 * those pages substitute it in, rather than hand-customizing per node.
 */
function Consents() {
    const { consents, loading, create, update, remove } = useConsentAdmin();
    const { legalEntityName, loading: entityLoading, update: updateEntityName } = useNodeSettingsAdmin();
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [required, setRequired] = useState(true);
    const [adding, setAdding] = useState(false);
    const [busyId, setBusyId] = useState(null);

    const canAdd = title.trim() && body.trim();

    const handleAdd = () => {
        if (!canAdd) return;
        setAdding(true);
        create({ title: title.trim(), body: body.trim(), required, sort_order: consents.length })
            .then(() => { message.success('Consent added — users will be asked on their next visit'); setTitle(''); setBody(''); setRequired(true); })
            .catch(err => message.error(err?.message || 'Could not add consent'))
            .finally(() => setAdding(false));
    };

    const patch = (row, changes, successMsg) => {
        setBusyId(row.id);
        return update(row.id, changes)
            .then(() => successMsg && message.success(successMsg))
            .catch(err => { message.error(err?.message || 'Could not update consent'); throw err; })
            .finally(() => setBusyId(null));
    };

    // Wording changes re-prompt everyone, so say so when it happens.
    const saveWording = (row, field) => value => patch(row, { [field]: value }, 'Updated — everyone will be asked to agree again');

    const columns = [
        {
            title: 'Consent',
            key: 'consent',
            render: (_, row) => (
                <div style={{ minWidth: 220 }}>
                    <InlineEditField
                        canEdit
                        required
                        value={row.title}
                        display={<Text strong>{row.title}</Text>}
                        placeholder="Title"
                        onSave={saveWording(row, 'title')}
                    />
                    <InlineEditField
                        canEdit
                        required
                        type="textarea"
                        value={row.body}
                        display={(
                            <Paragraph
                                type="secondary"
                                style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}
                                ellipsis={{ rows: 2, expandable: 'collapsible', symbol: expanded => expanded ? 'Show less' : 'Show more' }}
                            >
                                {row.body}
                            </Paragraph>
                        )}
                        placeholder="Wording"
                        onSave={saveWording(row, 'body')}
                    />
                </div>
            ),
        },
        {
            title: 'Required',
            key: 'required',
            render: (_, row) => (
                <Switch
                    size="small"
                    checked={row.required}
                    loading={busyId === row.id}
                    onChange={checked => patch(row, { required: checked }).catch(() => {})}
                />
            ),
        },
        {
            title: 'Enabled',
            key: 'enabled',
            render: (_, row) => (
                <Switch
                    size="small"
                    checked={row.enabled}
                    loading={busyId === row.id}
                    onChange={checked => patch(row, { enabled: checked }).catch(() => {})}
                />
            ),
        },
        {
            title: 'Accepted',
            key: 'accepted',
            render: (_, row) => (
                <Tooltip title={`People who accepted version ${row.version} (the current wording)`}>
                    <Space size={4}>
                        <Text>{row.accepted_count}</Text>
                        <Tag>v{row.version}</Tag>
                    </Space>
                </Tooltip>
            ),
        },
        {
            title: '',
            key: 'actions',
            align: 'right',
            render: (_, row) => (
                <Popconfirm
                    title={`Delete "${row.title}"?`}
                    description="This also deletes every recorded response to it. To stop asking without losing records, disable it instead."
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => remove(row.id)
                        .then(() => message.success('Consent deleted'))
                        .catch(() => message.error('Could not delete consent'))}
                >
                    <Button type="text" danger icon={<DeleteOutlined />} aria-label={`Delete ${row.title}`} />
                </Popconfirm>
            ),
        },
    ];

    return (
      <>
        <Card style={{ marginTop: 16 }}>
            <Title level={5} style={{ marginTop: 0 }}>Legal entity</Title>
            <Text type="secondary">
                The registered legal name of the organization that is the{' '}
                <Text strong>data controller</Text> for this node — the entity users
                contract with, named in the Terms of Use and Privacy Policy.
                {' '}{PLATFORM_LEGAL_ENTITY_NAME} builds and runs the underlying Vellby
                platform as this entity's <Text strong>data processor</Text> and is
                always disclosed as such — so don't enter {PLATFORM_LEGAL_ENTITY_NAME}
                {' '}here unless it is also the entity running this node. Leave it unset
                if no separate entity runs this node. Setting or changing this
                automatically adds or updates a required "Node operator &amp; platform
                provider" consent below, spelling the split out for users. Double-click
                to edit.
            </Text>
            <div style={{ marginTop: 12 }}>
                <InlineEditField
                    canEdit
                    required
                    value={legalEntityName}
                    display={entityLoading ? <Text type="secondary">Loading…</Text> : <Text strong>{legalEntityName || 'Not set'}</Text>}
                    placeholder="Legal entity name"
                    onSave={value => updateEntityName(value)
                        .then(() => message.success('Legal entity name updated'))
                        .catch(err => { message.error(err?.message || 'Could not update legal entity name'); throw err; })}
                />
            </div>
        </Card>

        <Card style={{ marginTop: 16 }}>
            <Title level={5} style={{ marginTop: 0 }}>Consents</Title>
            <Text type="secondary">
                Everyone is asked to respond to these before using the app — new users on
                first sign-in, existing users on their next visit. Required consents must be
                accepted to continue; optional ones can be declined. Editing a consent's title
                or wording asks everyone again. Double-click a title or wording to edit it.
            </Text>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '16px 0' }}>
                <Input placeholder="Title (e.g. Terms of use)" value={title} onChange={e => setTitle(e.target.value)} />
                <Input.TextArea
                    placeholder="Wording people agree to"
                    autoSize={{ minRows: 3, maxRows: 10 }}
                    value={body}
                    onChange={e => setBody(e.target.value)}
                />
                <Space>
                    <Checkbox checked={required} onChange={e => setRequired(e.target.checked)}>Required</Checkbox>
                    <Button type="primary" onClick={handleAdd} loading={adding} disabled={!canAdd}>Add consent</Button>
                </Space>
            </div>

            <Table
                rowKey="id"
                size="small"
                loading={loading}
                columns={columns}
                dataSource={consents}
                pagination={false}
                locale={{ emptyText: 'No consents yet — users are not prompted for anything' }}
            />
        </Card>
      </>
    );
}

export default Consents;
